/**
 * OrgSelectScreen — post-login organization picker for the web app.
 *
 * Mirrors `apps/mobile/src/screens/auth/OrgSelectScreen.tsx`. After the
 * user signs in, we fetch every organization they have access to via
 * `/my-organizations` and show them a list. If they only belong to one
 * org, or if their last selection (per UID) is in the list, we
 * auto-select. Otherwise they pick.
 *
 * The list view groups the stake and its wards into a single card, with
 * search, member counts, and a "last updated" line, and separates out
 * demo/sandbox orgs — see the `design_handoff_select_organization` design.
 * Grouping / relative-time / search live in `@leadership/types` (orgPicker)
 * so web + iOS can't drift. Light-only (the design's theme toggle is dropped).
 *
 * Rendered states:
 *   - loading        → spinner while we fetch / auto-select
 *   - error          → "Couldn't load organizations" + Try Again + Sign Out
 *   - empty          → "No Organizations" + Try Again + Sign Out
 *   - normal         → header + search + grouped list + Sandbox + footer
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import type { User } from 'firebase/auth'
import type { Organization } from '@leadership/types'
import { groupOrganizations, formatOrgUpdated, matchesOrgQuery } from '@leadership/types'
import { ROLE_DISPLAY_NAMES, STAKE_ROLE_DISPLAY_NAMES, getRoleDisplayName } from '@leadership/roles'
import Card from '../shared/ui/Card'
import Button from '../shared/ui/Button'
import { getSavedOrgId, setSavedOrgId } from '../shared/lib/orgStorage'

type Props = {
  user: User
  /**
   * Called when the user has successfully picked an organization. The
   * parent (App.tsx) is responsible for actually loading the org —
   * setting up the per-org Firestore listeners, transitioning to the
   * ward or stake stack, etc. This screen just persists the choice
   * and hands the org back, plus the total number of orgs the user
   * can access.
   */
  onSelected: (org: Organization, totalOrgs: number) => void
  onSignOut: () => void
  /**
   * Optional callback to open the SuperAdminPage. We only show the
   * "Super Admin" link when this prop is provided AND the user has
   * superadmin role on at least one org.
   */
  onOpenSuperAdmin?: () => void
}

// Role display names live in two parallel maps depending on org type.
function roleLabel(orgType: string | undefined, role: string | undefined): string {
  if (!role) return ''
  if (orgType === 'stake') return STAKE_ROLE_DISPLAY_NAMES[role] || getRoleDisplayName(role)
  return ROLE_DISPLAY_NAMES[role] || getRoleDisplayName(role)
}

// Two-letter initials for the signed-in avatar (falls back to email).
function initialsFor(user: User): string {
  const src = user.displayName || user.email || ''
  const parts = src.split(/[\s@._-]+/).filter(Boolean)
  const letters = parts.slice(0, 2).map((p) => p[0])
  return (letters.join('') || src.slice(0, 2)).toUpperCase()
}

const UsersIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" aria-hidden="true">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
)
const Chevron = () => (
  <svg
    className="os-chev"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    aria-hidden="true"
  >
    <polyline points="9 18 15 12 9 6" />
  </svg>
)

export default function OrgSelectScreen({ user, onSelected, onSignOut, onOpenSuperAdmin }: Props) {
  const [organizations, setOrganizations] = useState<Organization[]>([])
  const [fetching, setFetching] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const searchRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    void loadOrganizations()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ⌘K / Ctrl+K focuses the search box (matches the design).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        searchRef.current?.focus()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const { groups, demo } = useMemo(() => groupOrganizations(organizations), [organizations])

  async function loadOrganizations() {
    setFetching(true)
    setLoadError(null)
    try {
      const orgs: Organization[] = await window.apiRequest('/my-organizations')
      setOrganizations(orgs)

      // Auto-select chain, highest priority first:
      //   1. `?org=<id>` URL param (redirects from old subdomains, email invites)
      //   2. Single-org auto-select
      //   3. Saved-per-UID preference
      const url = new URL(window.location.href)
      const deepLinkOrgId = url.searchParams.get('org')
      if (deepLinkOrgId) {
        url.searchParams.delete('org')
        window.history.replaceState({}, '', url.toString())
        const target = orgs.find((o) => o.id === deepLinkOrgId)
        if (target) {
          handlePick(target, orgs)
          return
        }
      }

      if (orgs.length === 1) {
        handlePick(orgs[0], orgs)
        return
      }
      if (orgs.length > 1) {
        const savedId = getSavedOrgId(user.uid)
        if (savedId) {
          const saved = orgs.find((o) => o.id === savedId)
          if (saved) {
            handlePick(saved, orgs)
            return
          }
        }
      }
    } catch (e: any) {
      setLoadError(
        e?.message ||
          'Something went wrong while loading your organizations. Please check your connection and try again.',
      )
    } finally {
      setFetching(false)
    }
  }

  function handlePick(org: Organization, allOrgs: Organization[]) {
    setSavedOrgId(user.uid, org.id)
    onSelected(org, allOrgs.length)
  }

  // ── Loading ────────────────────────────────────────────────────────
  if (fetching) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-ocean-100 via-white to-slate-50 p-4">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-2 border-ocean-200 border-t-ocean-500 rounded-full animate-spin" />
          <p className="text-sm text-slate-500">Loading your organizations…</p>
        </div>
      </div>
    )
  }

  // ── Error ──────────────────────────────────────────────────────────
  if (loadError) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-ocean-100 via-white to-slate-50 p-4">
        <Card className="p-8 sm:p-10 max-w-md w-full text-center">
          <div className="w-14 h-14 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-5">
            <ion-icon name="warning-outline" style={{ fontSize: 28, color: '#dc2626' }}></ion-icon>
          </div>
          <h1 className="text-xl font-bold text-slate-900 mb-2">Couldn't load organizations</h1>
          <p className="text-sm text-slate-500 mb-2">{loadError}</p>
          {user.email ? (
            <p className="text-xs text-slate-400 mb-6">Signed in as {user.email}</p>
          ) : null}
          <div className="flex flex-col gap-2">
            <Button variant="primary" onClick={() => loadOrganizations()}>
              Try Again
            </Button>
            <Button variant="ghost" onClick={onSignOut}>
              Sign Out
            </Button>
          </div>
        </Card>
      </div>
    )
  }

  // ── Empty (legitimate "no invites yet" state) ──────────────────────
  if (organizations.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-ocean-100 via-white to-slate-50 p-4">
        <Card className="p-8 sm:p-10 max-w-md w-full text-center">
          <div className="w-14 h-14 bg-ocean-150 rounded-2xl flex items-center justify-center mx-auto mb-5">
            <ion-icon name="business-outline" style={{ fontSize: 28, color: '#4a8fa8' }}></ion-icon>
          </div>
          <h1 className="text-xl font-bold text-slate-900 mb-2">No Organizations</h1>
          <p className="text-sm text-slate-500 mb-2">
            You don't belong to any organizations yet. Ask your ward leadership to invite you.
          </p>
          {user.email ? (
            <p className="text-xs text-slate-400 mb-6">Signed in as {user.email}</p>
          ) : null}
          <div className="flex flex-col gap-2">
            <Button variant="secondary" onClick={() => loadOrganizations()}>
              Try Again
            </Button>
            <Button variant="ghost" onClick={onSignOut}>
              Sign Out
            </Button>
          </div>
        </Card>
      </div>
    )
  }

  // ── Org list (grouped, searchable) ─────────────────────────────────
  // Apply the live search filter per group; a group with no matching rows
  // is dropped, and if nothing matches at all we show the empty state.
  const visibleGroups = groups
    .map((g) => ({
      ...g,
      vStake: g.stake && matchesOrgQuery(g.stake, query) ? g.stake : null,
      vWards: g.wards.filter((w) => matchesOrgQuery(w, query)),
    }))
    .filter((g) => g.vStake || g.vWards.length > 0)
  const visibleDemo = demo.filter((o) => matchesOrgQuery(o, query))
  const nothingVisible = visibleGroups.length === 0 && visibleDemo.length === 0

  // Eyebrow = the stake's name when the user has exactly one real stake group.
  const soleStakeName =
    groups.length === 1 && groups[0].stake ? groups[0].stakeName : null

  const roleBadge = (org: Organization) => {
    if (org.role === 'superadmin')
      return <span className="os-badge os-badge-admin">Super Admin</span>
    if (org.role === 'stake_president')
      return <span className="os-badge os-badge-pres">Stake President</span>
    if (org.role) return <span className="os-badge os-badge-role">{roleLabel(org.type, org.role)}</span>
    return <span />
  }

  const renderRow = (
    org: Organization,
    opts: { stake?: boolean; demo?: boolean } = {},
  ) => {
    const updated = formatOrgUpdated(org.lastActivityAt)
    return (
      <button
        key={org.id}
        type="button"
        className={`os-row${opts.stake ? ' os-row-stake' : ''}`}
        onClick={() => handlePick(org, organizations)}
      >
        <span className="os-info">
          <span className="os-name">{org.name}</span>
          <span className="os-meta">
            <span className="os-type">{org.type === 'stake' ? 'Stake' : 'Ward'}</span>
            {updated ? (
              <>
                <span className="os-dot" />
                <span>{updated}</span>
              </>
            ) : null}
          </span>
        </span>
        <span className="os-users">
          {typeof org.members === 'number' ? (
            <>
              <UsersIcon />
              {org.members.toLocaleString('en-US')}
            </>
          ) : null}
        </span>
        {opts.demo ? <span className="os-badge os-badge-demo">Demo</span> : roleBadge(org)}
        <Chevron />
      </button>
    )
  }

  return (
    <div className="org-select-root">
      <style>{CSS}</style>
      <main className="os-page">
        <div className="os-head">
          <div className="os-head-main">
            {soleStakeName ? <div className="os-eyebrow">{soleStakeName}</div> : null}
            <h1 className="os-title">Select an organization</h1>
            <div className="os-signed">
              <span className="os-avatar">{initialsFor(user)}</span>
              <span>
                Signed in as <b>{user.email}</b>
              </span>
            </div>
          </div>
        </div>

        <div className="os-search">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            ref={searchRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search organizations…"
            autoComplete="off"
            aria-label="Search organizations"
          />
          <span className="os-kbd">⌘K</span>
        </div>

        {visibleGroups.map((g) => (
          <section className="os-group" key={g.stakeId ?? 'nostake'}>
            <div className="os-group-head">
              <span className="os-group-title">{g.stakeName}</span>
              <span className="os-group-count">
                · {g.vWards.length} ward{g.vWards.length === 1 ? '' : 's'}
              </span>
            </div>
            <div className="os-card">
              {g.vStake ? renderRow(g.vStake, { stake: true }) : null}
              {g.vWards.map((w) => renderRow(w))}
            </div>
          </section>
        ))}

        {visibleDemo.length > 0 ? (
          <section className="os-group os-group-demo">
            <div className="os-group-head">
              <span className="os-group-title">Sandbox</span>
              <span className="os-group-count">· training only</span>
            </div>
            <div className="os-card os-card-demo">{visibleDemo.map((o) => renderRow(o, { demo: true }))}</div>
            <div className="os-demo-note">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="16" x2="12" y2="12" />
                <line x1="12" y1="8" x2="12.01" y2="8" />
              </svg>
              <span>
                Demo organizations hold sample data for training. Nothing here affects live records.
              </span>
            </div>
          </section>
        ) : null}

        {nothingVisible ? (
          <div className="os-empty">No organizations match your search.</div>
        ) : null}

        <footer className="os-footer">
          {onOpenSuperAdmin && organizations.some((o) => o.role === 'superadmin') ? (
            <Button variant="ghost" onClick={onOpenSuperAdmin}>
              Super Admin
            </Button>
          ) : null}
          <Button variant="ghost" onClick={onSignOut}>
            Sign Out
          </Button>
        </footer>
      </main>
    </div>
  )
}

// Scoped styles — a light-only port of the design's tokens.css values, kept
// local to this screen (the rest of the web app is light-only and uses its
// own ocean palette). Same scoped-CSS pattern the recent redesigns use.
const CSS = `
.org-select-root {
  min-height: 100vh;
  background:
    radial-gradient(1200px 600px at 50% -10%, #eef2f8, transparent 70%),
    #f6f6f6;
}
.os-page { max-width: 664px; margin: 0 auto; padding: 56px 24px 96px; }
.os-head { display: flex; align-items: flex-start; gap: 16px; margin-bottom: 6px; }
.os-head-main { flex: 1; min-width: 0; }
.os-eyebrow { font-style: italic; font-size: 14px; color: #3a5685; letter-spacing: 0.01em; margin-bottom: 8px; }
.os-title { margin: 0; font-size: 30px; font-weight: 600; letter-spacing: -0.02em; color: #343434; line-height: 1.05; }
.os-signed { display: flex; align-items: center; gap: 8px; margin-top: 12px; font-size: 13.5px; color: #757575; }
.os-avatar { width: 24px; height: 24px; border-radius: 50%; background: #1d2e4a; color: #fff; font-size: 10px; font-weight: 600; display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0; }
.os-signed b { color: #4d4c4c; font-weight: 500; }
.os-search { display: flex; align-items: center; gap: 10px; margin: 28px 0 8px; background: #fff; border: 1px solid #e0e1e3; border-radius: 10px; padding: 0 14px; height: 44px; transition: box-shadow 120ms, border-color 120ms; }
.os-search:focus-within { border-color: #3a5685; box-shadow: 0 0 0 3px rgba(29,46,74,.18); }
.os-search > svg { width: 17px; height: 17px; color: #929292; flex-shrink: 0; }
.os-search input { flex: 1; border: none; background: transparent; outline: none; font: inherit; font-size: 14.5px; color: #343434; height: 100%; }
.os-search input::placeholder { color: #929292; }
.os-kbd { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 11px; color: #929292; border: 1px solid #e0e1e3; border-radius: 5px; padding: 2px 6px; }
.os-group { margin-top: 26px; }
.os-group-head { display: flex; align-items: baseline; gap: 8px; padding: 0 4px 10px; }
.os-group-title { font-size: 11px; font-weight: 600; letter-spacing: 0.09em; text-transform: uppercase; color: #757575; }
.os-group-count { font-size: 11px; font-weight: 500; letter-spacing: 0.06em; color: #929292; font-variant-numeric: tabular-nums; }
.os-card { background: #fff; border: 1px solid #ebecee; border-radius: 14px; overflow: hidden; box-shadow: 0 1px 2px rgba(0,0,0,.04), 0 1px 3px rgba(0,0,0,.05); }
.os-row { display: grid; grid-template-columns: 1fr auto auto 20px; align-items: center; gap: 14px; width: 100%; text-align: left; padding: 11px 16px; border: none; background: transparent; border-top: 1px solid #ebecee; font: inherit; cursor: pointer; color: #343434; transition: background 120ms; }
.os-row:first-child { border-top: none; }
.os-row:hover { background: #f3f3f3; }
.os-row:hover .os-chev { transform: translateX(2px); color: #4d4c4c; }
.os-info { min-width: 0; }
.os-name { font-size: 15px; font-weight: 500; color: #343434; letter-spacing: -0.01em; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.os-meta { display: flex; align-items: center; gap: 7px; margin-top: 3px; font-size: 12.5px; color: #929292; font-variant-numeric: tabular-nums; }
.os-meta .os-type { color: #757575; font-weight: 500; }
.os-dot { width: 3px; height: 3px; border-radius: 50%; background: #b8b8b8; flex-shrink: 0; }
.os-users { display: flex; align-items: center; gap: 5px; font-size: 13px; color: #757575; font-variant-numeric: tabular-nums; white-space: nowrap; }
.os-users svg { width: 14px; height: 14px; color: #a9a9a9; }
.os-badge { font-size: 11px; font-weight: 600; letter-spacing: 0.01em; padding: 3px 9px; border-radius: 999px; white-space: nowrap; }
.os-badge-admin { background: #eef2f8; color: #2b4168; }
.os-badge-pres { background: #f0e5ee; color: #7a4d78; }
.os-badge-role { background: #f3f3f3; color: #757575; }
.os-badge-demo { background: transparent; color: #929292; border: 1px solid #e0e1e3; font-weight: 500; }
.os-chev { width: 18px; height: 18px; color: #a9a9a9; justify-self: end; transition: transform 120ms, color 120ms; }
.os-row-stake .os-name { font-weight: 600; font-size: 15.5px; }
.os-group-demo .os-group-title { color: #929292; }
.os-card-demo { background: transparent; border: 1px dashed #d2d2d2; box-shadow: none; }
.os-card-demo .os-row { border-top: 1px dashed #ebecee; }
.os-card-demo .os-row:first-child { border-top: none; }
.os-card-demo .os-name { color: #4d4c4c; }
.os-demo-note { display: flex; align-items: center; gap: 7px; padding: 2px 4px 0; font-size: 12px; color: #929292; margin-top: 9px; }
.os-demo-note svg { width: 13px; height: 13px; flex-shrink: 0; }
.os-empty { text-align: center; padding: 40px 0; color: #929292; font-size: 14px; }
.os-footer { padding-top: 24px; text-align: center; display: flex; flex-direction: column; align-items: center; gap: 4px; }
@media (max-width: 560px) {
  .os-row { grid-template-columns: 1fr auto 20px; gap: 12px; }
  .os-users, .os-badge, .os-badge-admin, .os-badge-pres, .os-badge-role, .os-badge-demo { display: none; }
}
`
